import React, { memo, ReactNode } from 'react';

import styles from './RatingWithText.scss';
import cls from 'classnames';

import _round from 'lodash/round';

// на случай увеличения кол-ва состояний
type BadgeColor = 'neutral' | 'accent';

type Props = {
  className?: string;
  childrenClassNames?: {
    badge?: string;
    text?: string;
    extra?: string;
  };
  /** Дополнительный узел. Например, кол-во отзывов. */
  extra?: ReactNode;
  /** Форматирует значение рейтинга. По умолчанию: до десятых с точкой, кроме максимального значения */
  formatValue?: (value: number, max: number) => string;
  /** Возвращает цвет `badgeColor` в зависимости от оценки. По умолчания: `accent` от 6, остальное – `neutral`. */
  getBadgeColor?: (value: number) => BadgeColor;
  /** Текстовая оценка. */
  text?: ReactNode;
  /** Числовое значение рейтинга. Округляется до десятых, определяет цвет фона. */
  value: number;
  /** Минимальный допустимый для отображения рейтинг. */
  min?: number;
  /**
   * Максимальный допустимый для отображения рейтинг.
   * Используется в форматтере: если value === max, то не показываем ноль после точки
   * */
  max?: number;
  size?: 'default' | 'small';
  style?: React.CSSProperties;
};

// en - для точки в дробных
const formatValueDefault = (value: number, max: number): string =>
  Intl.NumberFormat('en', {
    minimumFractionDigits: _round(value, 1) === max ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(value);

const getBadgeColorDefault = (value: number): BadgeColor => {
  if (value > 6) {
    return 'accent';
  }

  return 'neutral';
};

const badgeStylesByColor: Record<BadgeColor, string> = {
  neutral: styles.isNeutral,
  accent: styles.isAccented,
};

const checkIfInRange = (value: number, min: number, max: number): boolean => {
  return value ? value >= min && value <= max : false;
};

const RatingWithText = ({
  childrenClassNames = {},
  className,
  extra,
  formatValue = formatValueDefault,
  getBadgeColor = getBadgeColorDefault,
  max = 10,
  min = 0,
  text,
  value,
  size = 'default',
  style = {},
}: Props) => {
  if (!checkIfInRange(value, min, max)) {
    return null;
  }

  const renderValue = () => {
    return (
      <div
        className={cls(
          styles.badgeWithValue,
          badgeStylesByColor[getBadgeColor(value)],
          childrenClassNames.badge
        )}
        style={style}
        data-locator="badge-with-value"
      >
        {formatValue(value, max)}
      </div>
    );
  };

  const renderText = () => {
    if (!text) {
      return null;
    }

    return (
      <div className={cls(styles.text, childrenClassNames.text)} data-locator="text">
        {text}
      </div>
    );
  };

  const renderExtra = () => {
    if (!extra) {
      return null;
    }

    return (
      <div className={cls(styles.extra, childrenClassNames.extra)} data-locator="extra">
        {extra}
      </div>
    );
  };

  return (
    <div
      className={cls(styles.container, className, {
        [styles.isSmall]: size === 'small',
      })}
      data-locator="rating-with-text"
    >
      {renderValue()}
      {renderText()}
      {renderExtra()}
    </div>
  );
};

export default memo(RatingWithText);
